// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useAutoFocus } from '@imprivata-cloud/common';
import { Button, InputBox } from '@imprivata-cloud/components';
import { Form, type InputRef } from 'antd';
import type React from 'react';
import { type ChangeEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  initValue?: string;
  disabled?: boolean;
  loading?: boolean;
  usernameLabel?: string;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  onSubmit: (username: string) => void;
}

export const UserNameForm = ({
  initValue = '',
  disabled = false,
  onSubmit,
  usernameLabel = '',
  loading = false,
  autoComplete,
}: IProps): JSX.Element => {
  const [username, setUsername] = useState(initValue);
  const { t } = useTranslation();
  const usernameRef = useAutoFocus<InputRef>();

  const formId = useId();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value.trim());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(username);
  };

  const finalizedLabel = usernameLabel
    ? t('username.custom-label', {
        username: usernameLabel,
        // Prevent HTML Entities from being screened twice - by i18n function and then by react
        interpolation: { escapeValue: false },
      })
    : t('username.label');

  useEffect(() => {
    const setFocus = () => {
      usernameRef?.current?.focus();
    };

    document.body.addEventListener('click', setFocus);
    return () => {
      document.body.removeEventListener('click', setFocus);
    };
  }, [usernameRef]);

  return (
    <form onSubmit={handleSubmit} data-testid="username-form" className="flex flex-column w-100">
      <div className="flex flex-column" style={{ gap: '1rem' }}>
        <h2 data-testid="username-input-label" className="factor-subtitle">
          {finalizedLabel}
        </h2>
        <Form.Item
          label={t('username.username')}
          colon={false}
          style={{ marginBottom: '0' }}
          layout="vertical"
        >
          <InputBox
            ref={usernameRef}
            type="text"
            placeholder={''}
            value={username}
            data-testid={'username-input'}
            onChange={handleChange}
            autoFocus={!disabled}
            width="100%"
            size={'middle'}
            className="primary-body"
            containerClassName="m-b-1"
            id={`usernameInput-${formId}`}
            maxLength={255}
            role="textbox"
            autoComplete={autoComplete}
          />
        </Form.Item>
      </div>
      <Button
        type="primary"
        size={'middle'}
        label={t('commons.continue-button') || undefined}
        disabled={!username.length || disabled}
        data-testid="submit-button"
        block={true}
        htmlType="submit"
        loading={loading}
      />
    </form>
  );
};
